import { useCallback, useMemo, useState } from "react";
import { useReactTable, getCoreRowModel, getExpandedRowModel } from "@tanstack/react-table";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const emptyData = [];

export const useTable = ({
    data = emptyData,
    columns: baseColumns,
    loading,
    sessionFiltersKey,
    rowIdProperty = "id",
    getRowProps,
    ...options
}) => {
    const [columnVisibility, setColumnVisibility] = useState(options?.initialState?.columnVisibility);
    const [expanded, setExpanded] = useState({});
    const columns = useMemo(() => {
        if (options?.canExpand) {
            return [
                {
                    id: "expand",
                    size: 10,
                    minSize: 10,
                    maxSize: 10,
                    enableHiding: false,
                    header: ({ table }) => {
                        const isAllExpanded = table.getIsAllRowsExpanded();
                        const icon = isAllExpanded ? faChevronUp : faChevronDown;
                        return (
                            <FontAwesomeIcon
                                icon={icon}
                                className="cursor-pointer text-xs"
                                onClick={table.getToggleAllRowsExpandedHandler()}
                            />
                        );
                    },
                    cell: ({ row }) => {
                        const isExpanded = row.getIsExpanded();
                        const icon = isExpanded ? faChevronUp : faChevronDown;

                        return (
                            <FontAwesomeIcon
                                icon={icon}
                                className="cursor-pointer text-xs px-3"
                                onClick={row.getToggleExpandedHandler()}
                            />
                        );
                    }
                },
                ...baseColumns
            ]
        }
        return baseColumns
    }, [baseColumns, options?.canExpand]);

    const table = useReactTable({
        columns,
        data,
        getCoreRowModel: getCoreRowModel(),
        state: { expanded, columnVisibility },
        getRowId: useCallback((row, _, parent) => {
            const id = row[rowIdProperty];
            if (parent) {
                return [parent.id, id].join('.');
            }
            return id;
        }, [rowIdProperty]),
        onColumnVisibilityChange: setColumnVisibility,
        onExpandedChange: setExpanded,
        getExpandedRowModel: getExpandedRowModel(),
        getRowCanExpand: () => true,
        manualPagination: true,
        manualFiltering: true,
        manualSorting: true,

        enableSorting: true,
        enableFilters: true,
        enableHiding: true,
        ...options,
    });

    return table;
};
